import { selectStandardProps } from "@/utils/componentUtils";
import { isDefined } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { FieldData } from "@rc-component/form/es/interface";
import { Col, Form, Input, Row, Select, Switch } from "antd";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BratislavaCityDistricts, KosiceCityDistricts, rowGutter } from "../../constants";
import { Country } from "../../enums";
import { Address } from "../../types";
import Ellipsis from "../views/Ellipsis";
import { AddressAutocomplete } from "./AddressAutocomplete";
import CountrySelect from "./CountrySelect";

const colSpan = 4;
const smallColSpan = 3;

const BA_CITY_NAME = "bratislava";
const KE_CITY_NAME = "košice";

const getDistrictOptions = (city?: string) => {
  if (city?.toLowerCase() === BA_CITY_NAME) {
    return BratislavaCityDistricts;
  } else if (city?.toLowerCase() === KE_CITY_NAME) {
    return KosiceCityDistricts;
  }

  return;
};

type AddressSwitchProps = {
  name: string;
};

type Props = {
  formItemsNamePath: (string | number)[];
  formFieldsNamePath?: (string | number)[];
  label: string;
  required?: boolean;
  switchProps?: AddressSwitchProps;
  hasInitialAddress?: boolean;
};

export const AddressForm = ({
  formItemsNamePath,
  formFieldsNamePath,
  label,
  required,
  switchProps,
  hasInitialAddress,
}: Props) => {
  const { t } = useTranslation();

  const [streetFilled, setStreetFilled] = useState<boolean>();
  const [checked, setChecked] = useState<boolean>(() => (isDefined(required) ? required : !!hasInitialAddress));

  const form = Form.useFormInstance();
  const fieldsNamePath = formFieldsNamePath ?? formItemsNamePath;
  const hasStreet = !!Form.useWatch([...fieldsNamePath, "street"], form);
  const city = Form.useWatch([...fieldsNamePath, "city"], form) as Address["city"];

  useEffect(() => {
    if (streetFilled) {
      form.validateFields([[...fieldsNamePath, streetFilled ? "descriptiveNumber" : "orientationNumber"]]);
    }
  }, [streetFilled]);

  const handleEnabledSwitchChange = (checked: boolean) => {
    if (!checked) {
      form.resetFields([
        [...fieldsNamePath, "street"],
        [...fieldsNamePath, "descriptiveNumber"],
        [...fieldsNamePath, "orientationNumber"],
        [...fieldsNamePath, "city"],
        [...fieldsNamePath, "zipCode"],
        [...fieldsNamePath, "country"],
      ]);
    } else {
      form.setFieldsValue({ [fieldsNamePath.toString()]: { country: Country.SVK } });
    }

    setChecked(checked);
  };

  const handleAddressAutocompleteFind = (address: Address) => {
    form.setFields(
      Object.keys(address).map<FieldData>((key) => ({
        name: [...fieldsNamePath, key],
        value: address[key as keyof Address],
      })),
    );
  };

  const switchButton = required ? undefined : (
    <span className="margin-left-tiny">
      {switchProps ? (
        <Form.Item name={switchProps.name} valuePropName="checked" initialValue={false} noStyle>
          <Switch size="small" onChange={handleEnabledSwitchChange} />
        </Form.Item>
      ) : (
        <Switch size="small" checked={checked} onChange={handleEnabledSwitchChange} />
      )}
    </span>
  );

  return (
    <>
      <Row gutter={rowGutter} className="margin-bottom-tiny">
        <Col style={{ lineHeight: "32px" }} className={classNames("bold-text", { "form-item-required": required })}>
          {label} {switchButton}
        </Col>
        <Col>
          <AddressAutocomplete hide={!checked} onAddressFind={handleAddressAutocompleteFind} />
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={[...formItemsNamePath, "street"]}
            label={t("address.attrs.street")}
            rules={[validations.size(1, 255), validations.pattern(regexPatterns.streetRegex)]}
          >
            <Input disabled={!checked} onChange={(e) => setStreetFilled(!!e.target.value)} />
          </Form.Item>
        </Col>

        <Col span={smallColSpan}>
          <Form.Item
            name={[...formItemsNamePath, "descriptiveNumber"]}
            label={t("address.attrs.descriptiveNumber")}
            rules={[
              hasStreet || !checked ? validations.none : validations.notBlank,
              validations.size(1, 64),
              validations.pattern(regexPatterns.streetNumberRegex),
            ]}
          >
            <Input disabled={!checked} />
          </Form.Item>
        </Col>

        <Col span={smallColSpan}>
          <Form.Item
            name={[...formItemsNamePath, "orientationNumber"]}
            label={<Ellipsis>{t("address.attrs.orientationNumber")}</Ellipsis>}
            rules={[
              checked && hasStreet ? validations.notBlank : validations.none,
              validations.size(1, 64),
              validations.pattern(regexPatterns.streetNumberRegex),
            ]}
          >
            <Input disabled={!checked} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={[...formItemsNamePath, "city"]}
            label={t("address.attrs.city")}
            rules={
              checked
                ? [validations.notBlank, validations.size(1, 64), validations.pattern(regexPatterns.wordRegex)]
                : [validations.none]
            }
          >
            <Input disabled={!checked} />
          </Form.Item>
        </Col>

        {[BA_CITY_NAME, KE_CITY_NAME].includes(city?.toLowerCase()) ? (
          <Col span={4}>
            <Form.Item
              name={[...formItemsNamePath, "cityDistrict"]}
              label={t("address.attrs.cityDistrict")}
              rules={[validations.none]}
            >
              <Select
                {...selectStandardProps}
                options={getDistrictOptions(city)?.map((district) => ({ value: district, label: district }))}
              />
            </Form.Item>
          </Col>
        ) : undefined}

        <Col span={smallColSpan}>
          <Form.Item
            name={[...formItemsNamePath, "zipCode"]}
            label={t("address.attrs.zipCode")}
            dependencies={[[...fieldsNamePath, "country"]]}
            rules={
              checked ? [validations.notBlank, validations.zipCode([...fieldsNamePath, "country"])] : [validations.none]
            }
          >
            <Input disabled={!checked} />
          </Form.Item>
        </Col>

        <Col span={smallColSpan}>
          <CountrySelect
            formItemProps={{
              name: [...formItemsNamePath, "country"],
              label: t("address.enums.country._label"),
              rules: checked ? [validations.notNull] : [validations.none],
            }}
            selectProps={{ disabled: !checked }}
          />
        </Col>
      </Row>
    </>
  );
};
